/**
 * AffiliateDashboard — the affiliate's referral codes.
 *
 * The legacy dashboard also showed Downloads and Total Earned columns, but the
 * referral-code endpoint returns neither, so they are omitted rather than shown
 * as a hardcoded zero.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import type { AffiliateReferralCode } from '@wlb/shared/api/affiliate/affiliateTypes';
import { useAffiliateSession } from '@wlb/shared/context/AffiliateSessionContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { ToastService } from '@wlb/shared/platform/notifier';
import type { ApiError } from '@wlb/shared/types';
import { getApiErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { Button } from '../components/ui/Button';
import { ListStatus } from '../components/ui/ListStatus';
import { Skeleton, SkeletonGroup, skeletonKeys } from '../components/ui/Skeleton';
import { PlusIcon, RefreshIcon, UserIcon } from '../components/ui/icons';
import { AffiliatePortalLayout } from '../features/affiliate/AffiliatePortalLayout';
import { AFFILIATE_ROUTES } from '../features/affiliate/affiliateRoutes';
import { ReferralCodeDialog } from '../features/affiliate/ReferralCodeDialog';
import '../features/affiliate/affiliatePortal.css';

const CURRENCY_FORMAT = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
});

export function AffiliateDashboard() {
  const { t } = useTranslation();
  const { session, auth } = useAffiliateSession();
  const affiliateUserId = session?.affiliateUserId ?? 0;

  const [codes, setCodes] = useState<AffiliateReferralCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<AffiliateReferralCode | null>(null);
  const [deactivatingId, setDeactivatingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    if (!affiliateUserId) return;
    setLoading(true);
    setError('');
    try {
      setCodes(await affiliateService.getReferralCodes(affiliateUserId, auth));
    } catch (caught) {
      setError(
        getApiErrorMessage(caught as ApiError) || t('affiliate.dashboard.loadError'),
      );
    } finally {
      setLoading(false);
    }
  }, [affiliateUserId, auth, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDeactivate = async (code: AffiliateReferralCode) => {
    setDeactivatingId(code.id);
    try {
      await affiliateService.deleteReferralCode(code.id, affiliateUserId, auth);
      ToastService.show({
        type: 'success',
        message: t('affiliate.dashboard.deactivated'),
      });
      await load();
    } catch (caught) {
      ToastService.show({
        type: 'error',
        message:
          getApiErrorMessage(caught as ApiError) ||
          t('affiliate.dashboard.deactivateFailed'),
      });
    } finally {
      setDeactivatingId(null);
    }
  };

  return (
    <AffiliatePortalLayout
      headerActions={
        <Link
          className="affiliate-portal__header-link"
          to={AFFILIATE_ROUTES.PROFILE}
          aria-label={t('affiliate.dashboard.openProfile')}
        >
          <UserIcon size={18} />
          {t('affiliate.profile.heading')}
        </Link>
      }
    >
      <div className="affiliate-dashboard__header">
        <div>
          <h1 className="affiliate-dashboard__title">{t('affiliate.dashboard.title')}</h1>
          {session?.email && (
            <p className="affiliate-dashboard__subtitle">{session.email}</p>
          )}
        </div>
      </div>

      <section className="affiliate-dashboard__panel">
        <div className="affiliate-dashboard__panel-header">
          <h2 className="affiliate-dashboard__panel-title">
            {t('affiliate.dashboard.referralCodesTitle')}
          </h2>
          <div className="affiliate-dashboard__actions">
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              aria-label={t('affiliate.dashboard.refresh')}
              title={t('affiliate.dashboard.refresh')}
            >
              <RefreshIcon size={16} />
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
            >
              <PlusIcon size={16} />
              {t('affiliate.dashboard.addCode')}
            </Button>
          </div>
        </div>

        {loading && (
          <SkeletonGroup className="affiliate-dashboard__skeleton">
            {skeletonKeys(4).map(key => (
              <Skeleton key={key} className="affiliate-dashboard__skeleton-row" />
            ))}
          </SkeletonGroup>
        )}

        {!loading && error && (
          <ListStatus variant="error" message={error} onAction={load} />
        )}

        {!loading && !error && codes.length === 0 && (
          <ListStatus
            variant="empty"
            title={t('affiliate.dashboard.emptyTitle')}
            message={t('affiliate.dashboard.emptyMessage')}
          />
        )}

        {!loading && !error && codes.length > 0 && (
          <div className="affiliate-dashboard__table-scroll">
            <table className="affiliate-table">
              <caption className="sr-only">{t('affiliate.dashboard.tableCaption')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('affiliate.dashboard.codeColumn')}</th>
                  <th scope="col">{t('affiliate.dashboard.descriptionColumn')}</th>
                  <th scope="col">{t('affiliate.dashboard.commissionColumn')}</th>
                  <th scope="col">{t('affiliate.dashboard.statusColumn')}</th>
                  <th scope="col">{t('affiliate.dashboard.actionsColumn')}</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(code => (
                  <tr key={code.id}>
                    <td className="affiliate-table__code">{code.code}</td>
                    <td>{code.description || t('common.notProvided')}</td>
                    <td>{CURRENCY_FORMAT.format(code.commissionPerDownload)}</td>
                    <td>
                      <span
                        className={`affiliate-status-pill affiliate-status-pill--${
                          code.isActive ? 'active' : 'inactive'
                        }`}
                      >
                        {t(
                          code.isActive
                            ? 'affiliate.dashboard.statusActive'
                            : 'affiliate.dashboard.statusInactive',
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="affiliate-table__row-actions">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditing(code);
                            setDialogOpen(true);
                          }}
                          aria-label={t('affiliate.accessibility.editReferralCode', {
                            code: code.code,
                          })}
                        >
                          {t('common.buttons.edit')}
                        </Button>
                        {code.isActive && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivate(code)}
                            disabled={deactivatingId === code.id}
                            aria-label={t(
                              'affiliate.accessibility.deactivateReferralCode',
                              { code: code.code },
                            )}
                          >
                            {deactivatingId === code.id
                              ? t('common.loading')
                              : t('affiliate.dashboard.deactivate')}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {dialogOpen && (
        <ReferralCodeDialog
          affiliateUserId={affiliateUserId}
          affiliateName={session?.email ?? ''}
          auth={auth}
          code={editing}
          onClose={() => setDialogOpen(false)}
          onSaved={() => {
            setDialogOpen(false);
            load();
          }}
        />
      )}
    </AffiliatePortalLayout>
  );
}
